import { can } from "@bezbolisno/kit/rbac";
import { Card } from "@bezbolisno/ui/card";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { api, expectOk } from "../../lib/api";
import { keys } from "../../lib/keys";
import { ClinicalDashboard, ManagerDashboard } from "../../screens/dashboard/dashboard";

/** `?view=admin` (RoleToggle) — the legacy `?role=admin` still works. The API only honours it for admins. */
const Search = z.object({
  view: z.enum(["admin"]).optional(),
  role: z.enum(["admin"]).optional(),
});

const dashboardQuery = (view: "admin" | undefined) =>
  queryOptions({
    queryKey: keys.dashboard(view ?? "default"),
    queryFn: async () =>
      expectOk(await api.readModels.dashboard({ query: view ? { view } : {} }), 200),
  });

export const Route = createFileRoute("/_authed/")({
  validateSearch: Search,
  loaderDeps: ({ search }) => ({ view: search.view ?? search.role }),
  loader: ({ context, deps }) => context.queryClient.ensureQueryData(dashboardQuery(deps.view)),
  component: DashboardPage,
  errorComponent: () => (
    <div className="fade-in">
      <Card className="p-5">
        <b>База даних ще не піднята.</b>
        <p className="text-sm text-[color:var(--muted)] mt-2 leading-relaxed">
          Запусти <code>docker compose up -d</code>, потім <code>pnpm db:push</code> та{" "}
          <code>pnpm db:seed</code>.
        </p>
      </Card>
    </div>
  ),
});

function DashboardPage() {
  const { view, role } = Route.useSearch();
  const { session } = Route.useRouteContext();
  const { data } = useSuspenseQuery(dashboardQuery(view ?? role));
  const canBill = can(session.role, "invoice:manage");

  return (
    <div className="fade-in">
      {data.view === "manager" ? (
        <ManagerDashboard data={data} canBill={canBill} userName={session.firstName} />
      ) : (
        <ClinicalDashboard data={data} canBill={canBill} userName={session.firstName} />
      )}
    </div>
  );
}
